/**
 * QuickCaptureModal — Cmd+T capture dialog (F 72 MVP phase 4)
 *
 * Lightweight modal with a single text input. Type a task name, hit
 * Enter → creates a Task with status='inbox' via TaskStore, closes.
 * Esc cancels.
 *
 * Why a modal rather than an inline overlay?
 *   - Cmd+T (and Ctrl+T) is already used by browsers to open a new tab.
 *     We preventDefault in the handler so the page does not navigate.
 *   - A modal is focus-trapped and accessible without needing
 *     elaborate a11y plumbing for v1.
 *
 * Mounted at App root with a window-level keydown handler.
 *
 * F 79: Now renders via createPortal to <body> so it escapes any
 * stacking-context interference from parent flex columns.
 * Backdrop dims the app; panel is centered with elevation.
 * Focus trap keeps keyboard navigation inside the modal.
 *
 * F 99.1 — REFACTORED to use the generic <Modal> + <Backdrop> primitives
 * (F 96 design). Domain logic (Task creation) stays here; the chrome
 * (panel, backdrop, focus trap, Escape close) is delegated to <Modal>.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createTask } from '../../../types/models/Task';
import { TaskStore } from '../../../core/task/store';
import { Modal } from '../../primitives/Modal';
import './QuickCaptureModal.css';

export interface QuickCaptureModalProps {
  store?: TaskStore;
  opened: boolean;
  onClose: () => void;
  /** Optional pre-fill (e.g. for editor-anchored capture). */
  initialTitle?: string;
}

export const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({
  store,
  opened,
  onClose,
  initialTitle,
}) => {
  const internalStore = React.useMemo(() => store ?? new TaskStore(), [store]);
  const [title, setTitle] = useState(initialTitle ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset + focus when opening.
  useEffect(() => {
    if (opened) {
      setTitle(initialTitle ?? '');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [opened, initialTitle]);

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      onClose();
      return;
    }
    const task = createTask({ title: trimmed, status: 'inbox' });
    await internalStore.save(task);
    setTitle('');
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void onSubmit();
    }
    // Escape is handled by <Modal> via its built-in closeOnEscape.
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      maxWidth={420}
      padding={20}
      borderRadius={12}
      zIndex={1000}
      ariaLabel="Quick capture"
      closeLabel="Close quick capture"
      header={
        <div className="fc-quick-capture__label">
          New task
          <span className="fc-quick-capture__label-hint">will be added to Inbox</span>
        </div>
      }
    >
      <form onSubmit={onSubmit}>
        <input
          id="fc-quick-capture-input"
          ref={inputRef}
          className="fc-quick-capture__input"
          type="text"
          placeholder="What needs doing?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          autoFocus
        />
        <div className="fc-quick-capture__hint">
          <kbd>Enter</kbd> to capture · <kbd>Esc</kbd> to dismiss
        </div>
      </form>
    </Modal>
  );
};

export default QuickCaptureModal;